import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Result, Spin, StepProps, Steps, StepsProps } from "antd";
import useIsMobile from "../../utils/useIsMobile";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import StepComponent from "../Step";
import { ConfigStepType } from "../../utils/configuration";
import { useSearchParams } from "react-router";
import { Applicant, createApplicant, GetApplicant, getApplicant, SET_CURRENT_STEP, Tokens } from "../../redux/reducer/applicant";
import TinGate from "../organisms/TinGate";
import { AxiosResponse } from "axios";
import { requestAuthRecovery } from "../../utils/authRecovery";
import { debug } from "../../utils/debug";
import { activateStoredSession, applicantIdFromToken, isTokenUsable, storeTokens, tokenMatchesId } from "../../utils/tokenStore";
import { get } from "../../redux/http/request";
import { ERROR_MESSAGE } from "../../redux/reducer/status";

interface OrchestratorParams {
}

const Orchestrator = ({
}: OrchestratorParams) => {
    const dispatch = useDispatch<AppDispatch>();
    const isMobile = useIsMobile();
    const [params] = useSearchParams();
    const [sessionUnrecoverable, setSessionUnrecoverable] = useState(false);
    const [authAgain, setAuthAgain] = useState<number>(0);
    const [tinVerified, setTinVerified] = useState<boolean>(false);
    const institutionId = useSelector<RootState, string | undefined>(({ configuration }) => configuration.institution?.institutionId);
    const requireLast4Tin = useSelector<RootState, boolean>(({ configuration }) => configuration.institution?.requireLast4Tin ?? false);
    const applicant = useSelector<RootState, Applicant | null>(({ applicant: a }) => a.applicant);
    const currentStep = useSelector<RootState, number>(({ applicant: a }) => a.currentStep);
    const services = useSelector<RootState, ConfigStepType[]>(({ configuration }) => configuration.steps ?? []);
    const errorSteps = useSelector<RootState, { [id: string]: boolean }>(({ applicant: a }) => a.errorSteps ?? {});
    const activeServices = useMemo<ConfigStepType[]>(() => {
        if (!applicant?.steps) return services;
        const applicantStepIds = new Set(applicant.steps.map(s => s.id));
        return services.filter(s => applicantStepIds.has(s.id));
    }, [services, applicant?.steps]);
    const applicantId = useMemo(() => (
        applicant?.applicantId ?? params.get('id') ?? applicantIdFromToken(localStorage.getItem('accessToken'))
    ), [applicant, params]);
    // The partner's own id, never the ?id= param — see requestAuthRecovery.
    const partnerApplicantId = useMemo(() => {
        if (applicant?.applicantId) return applicant.applicantId;
        const stored = localStorage.getItem('accessToken');
        const paramId = params.get('id');
        // don't offer up another applicant's id just because their token is still in storage
        if (paramId && !tokenMatchesId(stored, paramId)) return null;
        return applicantIdFromToken(stored);
    }, [applicant, params]);
    const hasApplicant = useSelector<RootState, boolean>(({ applicant: { applicant: a } }) => Boolean(a));

    // Track previous step to detect transitions for postMessage
    const prevStepRef = useRef<number | null>(null);

    useEffect(() => {
        // Skip if not in iframe
        if (window.parent === window) return;

        // Skip initial render
        if (prevStepRef.current === null) {
            prevStepRef.current = currentStep;
            return;
        }

        // Only post if step actually increased (step completed)
        if (currentStep > prevStepRef.current && activeServices.length > 0) {
            const completedStepIndex = prevStepRef.current;
            const completedStepConfig = activeServices[completedStepIndex];

            try {
                window.parent.postMessage({
                    type: 'step-completed',
                    source: 'halcyondirect',
                    completedStep: completedStepConfig ? {
                        index: completedStepIndex,
                        id: completedStepConfig.id,
                        name: completedStepConfig.nameOverride ?? completedStepConfig.name,
                    } : null,
                    nextStep: activeServices[currentStep] ? {
                        index: currentStep,
                        id: activeServices[currentStep].id,
                        name: activeServices[currentStep].nameOverride ?? activeServices[currentStep].name,
                    } : null,
                    allComplete: currentStep >= activeServices.length,
                }, '*');
            } catch (error) {
                console.error('Error sending step-completed message to parent:', error);
            }
        }

        prevStepRef.current = currentStep;
    }, [currentStep, activeServices]);

    const setCurrentStep = useCallback((newStep: number) => {
        dispatch({ type: SET_CURRENT_STEP, newStep })
    }, [dispatch]);

    const hasCalled = useRef(-1);

    const makeApplicant = useCallback(() => {
        const createApplicantId = params.get('id');
        const cId = params.get('cid') ?? undefined;
        const bundleId = params.get('bundleId') ?? undefined;
        const encompassBorrowerId = params.get('borrowerId') ?? undefined;
        const pii = {
            name: params.get('name') ?? undefined,
            firstName: params.get('firstName') ?? params.get('firstname') ?? undefined,
            lastName: params.get('lastName') ?? params.get('lastname') ?? undefined,
            tin: params.get('tin') ?? undefined,
            email: params.get('email') ?? undefined,
            addressLine1: params.get('addressLine1') ?? params.get('addressline1') ?? undefined,
            addressLine2: params.get('addressLine2') ?? params.get('addressline2') ?? undefined,
            city: params.get('city') ?? undefined,
            state: params.get('state') ?? undefined,
            postalCode: params.get('postalCode') ?? params.get('postalcode') ?? undefined,
        }

        // Parse businesses from URL params
        let businesses: any[] | undefined;
        const businessesParam = params.get('businesses');
        if (businessesParam) {
            try {
                businesses = JSON.parse(decodeURIComponent(businessesParam));
            } catch {
                console.warn('Failed to parse businesses from URL params');
            }
        }

        dispatch(createApplicant({
            input: {
                institutionId: institutionId ?? '',
                applicantId: createApplicantId ?? '',
                cId,
                pii,
                encompassBorrowerId,
                bundleId,
                businesses,
            },
            onSuccess: (res: AxiosResponse<{ applicant: Applicant; tokens?: Tokens; }>) => {
                if (res.data.tokens) storeTokens(res.data.tokens);
            }
        }));
    }, [params, applicant]);

    useEffect(() => {
        // Guards StrictMode's double-invoke without blocking a deliberate re-run: a refresh bumps
        // authAgain precisely so this runs again with the new token. A plain ref was never reset,
        // so the second pass bailed and getApplicant was never dispatched — an applicant returning
        // between day 1 and day 7 refreshed successfully and then sat on the spinner forever.
        if (hasCalled.current === authAgain) return;
        hasCalled.current = authAgain;

        if (institutionId) {
            const input: GetApplicant = {
                applicantId,
                institutionId,
            };
            const tempToken = params.get('token');
            if (tempToken) input.token = tempToken;
            const headers: { Authorization?: string; refresh?: string } = {};
            let shouldCall = true;
            // Whatever is current in storage may belong to somebody else entirely — this browser
            // is reused across applicants — so hold the pair locally and resolve it against ?id=
            // before trusting either half of it.
            let accessToken = localStorage.getItem('accessToken');
            let refreshToken = localStorage.getItem('refreshToken');

            const paramId = params.get('id');
            if (paramId && !tokenMatchesId(accessToken, paramId)) {
                // Swap first: this browser may hold a live session for the applicant in ?id= even
                // though the current one is dead. Judging credentials before looking would signal a
                // recovery the parent does not need.
                const matched = activateStoredSession(paramId);
                if (matched) {
                    accessToken = matched;
                    refreshToken = localStorage.getItem('refreshToken'); // activateStoredSession moved it
                    headers.Authorization = `Bearer ${accessToken}`;
                } else {
                    // Nothing stored belongs to this applicant. The tokens still sitting in storage
                    // are somebody else's: refreshing one asks the API to reissue for the wrong
                    // person, and sending one fails the uid check. This load has no credential.
                    debug('auth', `stored session is for another applicant, ignoring it for ${paramId}`);
                    accessToken = null;
                    refreshToken = null;
                }
            }

            debug('auth', 'evaluating stored credentials', {
                accessUsable: isTokenUsable(accessToken),
                hasAccessToken: Boolean(accessToken),
                refreshUsable: isTokenUsable(refreshToken),
                hasUrlToken: Boolean(tempToken),
            });

            if (isTokenUsable(accessToken)) {
                debug('auth', 'access token is live, using it as-is');
                headers.Authorization = `Bearer ${accessToken ?? ''}`;
            }
            else if (accessToken && isTokenUsable(refreshToken)) {
                shouldCall = false;
                debug('auth', 'access token is stale, refreshing');
                get('/auth', {}, undefined, { refresh: refreshToken })
                    .then((res) => {
                        const tokens: Tokens | undefined = res.data.tokens;
                        if (!tokens?.accessToken) throw new Error('refresh returned no tokens');
                        debug('auth', 'refresh succeeded', { rotated: Boolean(tokens.refreshToken) });
                        storeTokens(tokens);
                        setAuthAgain((n) => n + 1);
                    })
                    // a dead refresh leaves nothing to retry with, so ask the parent for a new opaque token
                    .catch((error) => {
                        debug('auth', 'refresh failed', error?.response?.status ?? error);
                        requestAuthRecovery(applicantId, 'refresh-failed', partnerApplicantId);
                    });
            } else if (tempToken) {
                debug('auth', 'no usable credential, but a ?token= is present to exchange');
            } else if (accessToken) {
                // had a session for THIS applicant, both tokens are now unusable, and there is no
                // opaque token to exchange. Requiring an access token keeps a brand-new applicant
                // (created from URL params, no tokens yet) out of here; the ?token= guard stops a
                // recovery reload from immediately signalling again.
                requestAuthRecovery(applicantId, 'no-token', partnerApplicantId);
            } else {
                debug('auth', 'no stored session — treating as a first visit, not an expiry');
            }

            if (shouldCall) {
                // Either we were handed a code to redeem or we had a session that has died.
                // A pristine first visit has neither, and must not be told anything was lost.
                const isRecoveryAttempt = Boolean(tempToken) || Boolean(accessToken);
                const sentCredential = Boolean(headers.Authorization) || Boolean(tempToken);

                const unrecoverable = (why: string, force = false) => {
                    debug('auth', `session could not be restored: ${why}`, { isRecoveryAttempt, force });
                    if (!isRecoveryAttempt && !force) return;
                    setSessionUnrecoverable(true);
                    // the dedicated screen says it better than the generic toast
                    dispatch({ type: ERROR_MESSAGE });
                };

                dispatch(getApplicant({
                    input,
                    headers,
                    onFailure: (err: any) => {
                        const status = err?.status ?? err?.response?.status;
                        // An applicant we do not know 200s with createOnNull, so a 401 here means
                        // the applicant exists and we simply cannot prove it — storage cleared, or
                        // a different device. Recoverable, and previously an endless spinner.
                        const knownApplicantNoCredential = !sentCredential && (status === 401 || status === 419);
                        if (knownApplicantNoCredential) {
                            debug('auth', 'known applicant, no credential to offer — asking the parent');
                            requestAuthRecovery(applicantId, 'no-token', partnerApplicantId);
                        }
                        unrecoverable('the applicant request failed', knownApplicantNoCredential);
                    },
                    onSuccess: (res: AxiosResponse<{ applicant: Applicant | null; createOnNull: boolean; tokens?: Tokens }>) => {
                        if (res.status === 200 && res.data.applicant === null && res.data.createOnNull && (institutionId && applicantId)) {
                            makeApplicant();
                        } else if (res.status === 200 && res.data.applicant === null) {
                            // no applicant and no permission to create one — nothing to render
                            unrecoverable('no applicant, and anonymous create is off');
                        } else if (res.status === 200 && res.data.tokens) {
                            storeTokens(res.data.tokens);
                        }
                    }
                }));
            }
        }
    }, [authAgain]);

    const items = useMemo<StepsProps['items']>(() => activeServices.map<StepProps>((step) => ({
        title: step.nameOverride ?? step.name,
        status: errorSteps[step.id] ? 'error' : undefined,
    })), [activeServices, errorSteps]);

    if (sessionUnrecoverable && !hasApplicant) {
        return (
            <Result
                status="warning"
                title="We couldn't restore your session"
                subTitle="Your sign-in has expired and we weren't able to renew it automatically. Reopening the link you were sent will start a new session."
                extra={(
                    <Button type="primary" onClick={() => { window.location.reload(); }}>
                        Try again
                    </Button>
                )}
            />
        );
    }

    if (hasApplicant && requireLast4Tin && !tinVerified && institutionId && applicantId) {
        return <TinGate institutionId={institutionId} applicantId={applicantId} onVerified={() => setTinVerified(true)} />;
    }

    return hasApplicant ? (
        <div className={isMobile ? "py-8" : "pa-8"}>
            {
                currentStep >= activeServices.length ? (
                    <Result
                        status="success"
                        title="You are all set!"
                    />
                ) : (
                    <>
                        <Steps
                            className='pa-4 mb-4'
                            items={items}
                            current={currentStep}
                        />
                        {activeServices[currentStep] && (
                            <StepComponent
                                step={activeServices[currentStep]}
                                onContinue={() => { setCurrentStep(currentStep + 1); }}
                            />)}
                    </>
                )
            }
        </div>
    ) : <Spin fullscreen />;
};

export default Orchestrator;
